import React, { useState } from "react";
import "../styles/providerById.css";
import { SERVER_URL } from "../config";

interface Provider {
  id: string;
  firstName: string;
  lastName: string;
  specialty?: string;
  licenseNumber: string;
  createdAt: string;
  updatedAt?: string;
  user: {
    email: string;
    role?: string;
    // The extended details returned by the route:
    providerCoupons?: any[];
    prescriptionsAsProvider?: any[];
    PaymentMethod?: any[];
  };
  clinic?: {
    clinicName: string;
    address: string;
    phone: string;
    region: string;
    clinicCode: string;
  };
}

interface ProviderByIdProps {
  providerData: Provider;
}

const ProviderById: React.FC<ProviderByIdProps> = ({ providerData }) => {
  // State to store additional fetched details.
  // We'll use the fetched object to update the initially passed provider data.
  const [additionalDetails, setAdditionalDetails] = useState<Provider | null>(
    null
  );
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [errorMore, setErrorMore] = useState<string | null>(null);
  const [showMore, setShowMore] = useState<boolean>(false);

  const handleFetchMoreDetails = async () => {
    setLoadingMore(true);
    setErrorMore(null);
    try {
      const response = await fetch(
        `${SERVER_URL}/api/provider/providers/${providerData.id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch additional provider details.");
      }
      const data: Provider = await response.json();
      setAdditionalDetails(data);
      setShowMore(true);
    } catch (err: any) {
      console.error("Error fetching additional details:", err);
      setErrorMore(err.message || "Error fetching additional details.");
    } finally {
      setLoadingMore(false);
    }
  };

  // Use additionalDetails if available; otherwise fall back to the passed prop.
  const details = additionalDetails || providerData;

  return (
    <div className="provider-by-id-container">
      <h2>
        {details.firstName} {details.lastName}
      </h2>
      <table className="provider-details-table">
        <tbody>
          <tr>
            <td>
              <strong>Email</strong>
            </td>
            <td>{details.user.email}</td>
          </tr>
          <tr>
            <td>
              <strong>Specialty</strong>
            </td>
            <td>{details.specialty || "N/A"}</td>
          </tr>
          <tr>
            <td>
              <strong>License Number</strong>
            </td>
            <td>{details.licenseNumber}</td>
          </tr>
          <tr>
            <td>
              <strong>Joined On</strong>
            </td>
            <td>{new Date(details.createdAt).toLocaleDateString()}</td>
          </tr>
          {details.updatedAt && (
            <tr>
              <td>
                <strong>Updated On</strong>
              </td>
              <td>{new Date(details.updatedAt).toLocaleDateString()}</td>
            </tr>
          )}
          <tr>
            <td>
              <strong>User Role</strong>
            </td>
            <td>{details.user.role || "N/A"}</td>
          </tr>
        </tbody>
      </table>

      {details.clinic && (
        <>
          <h3>Clinic Affiliation</h3>
          <table className="clinic-details-table">
            <tbody>
              <tr>
                <td>
                  <strong>Clinic Name</strong>
                </td>
                <td>{details.clinic.clinicName}</td>
              </tr>
              <tr>
                <td>
                  <strong>Address</strong>
                </td>
                <td>{details.clinic.address}</td>
              </tr>
              <tr>
                <td>
                  <strong>Phone</strong>
                </td>
                <td>{details.clinic.phone}</td>
              </tr>
              <tr>
                <td>
                  <strong>Region</strong>
                </td>
                <td>{details.clinic.region}</td>
              </tr>
              <tr>
                <td>
                  <strong>Clinic Code</strong>
                </td>
                <td>{details.clinic.clinicCode}</td>
              </tr>
            </tbody>
          </table>
        </>
      )}

      <div className="more-details-container">
        {!showMore && !loadingMore && (
          <button className="view-more-button" onClick={handleFetchMoreDetails}>
            View More Details
          </button>
        )}
        {loadingMore && (
          <p className="loading-text">Loading additional details...</p>
        )}
        {errorMore && <p className="error-text">{errorMore}</p>}
        {showMore && additionalDetails && (
          <>
            <h3>Additional Provider Details</h3>
            <table className="additional-details-table">
              <tbody>
                <tr>
                  <td>
                    <strong>Provider Coupons</strong>
                  </td>
                  <td>
                    {additionalDetails.user.providerCoupons &&
                    additionalDetails.user.providerCoupons.length > 0 ? (
                      <ul>
                        {additionalDetails.user.providerCoupons.map(
                          (coupon: any) => (
                            <li key={coupon.id}>
                              {/* Render coupon details as needed. Adjust this rendering */}
                              {coupon.description || JSON.stringify(coupon)}
                            </li>
                          )
                        )}
                      </ul>
                    ) : (
                      "None"
                    )}
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Prescriptions As Provider</strong>
                  </td>
                  <td>
                    {additionalDetails.user.prescriptionsAsProvider &&
                    additionalDetails.user.prescriptionsAsProvider.length >
                      0 ? (
                      <ul>
                        {additionalDetails.user.prescriptionsAsProvider.map(
                          (prescription: any) => (
                            <li key={prescription.id}>
                              {/* Customize the displayed prescription info */}
                              {prescription.prescriptionNumber ||
                                JSON.stringify(prescription)}
                            </li>
                          )
                        )}
                      </ul>
                    ) : (
                      "None"
                    )}
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Payment Methods</strong>
                  </td>
                  <td>
                    {additionalDetails.user.PaymentMethod &&
                    additionalDetails.user.PaymentMethod.length > 0 ? (
                      <ul>
                        {additionalDetails.user.PaymentMethod.map(
                          (method: any) => (
                            <li key={method.id}>
                              {/* Adjust this according to your PaymentMethod schema */}
                              {method.cardBrand
                                ? `${method.cardBrand} ending in ${method.last4}`
                                : JSON.stringify(method)}
                            </li>
                          )
                        )}
                      </ul>
                    ) : (
                      "None"
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
            <button
              className="hide-more-button"
              onClick={() => setShowMore(false)}
            >
              Hide Additional Details
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ProviderById;
